// src/pages/ExpenseManagement.tsx
import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
  Plus,
  Filter,
  Eye,
  Edit,
  Trash2,
  Check,
  X,
  Clock,
  DollarSign,
  FileText,
  Calendar,
  Building2,
  Tag,
  CreditCard,
  CheckCircle,
} from "lucide-react";
import { expenseService } from "../services/expenses";
import { getBranches } from "../services/branches";
import { useAuth } from "../hooks/useAuth";
import Input from "../components/common/Input";
import {
  Expense,
  CreateExpenseData,
  ExpenseFilters,
  ExpenseCategory,
  PaymentMethod,
  ApprovalStatus,
} from "../types/expense";
import { Branch } from "../types/branch";
import { useHorizontalDragScroll } from "../hooks/useHorizontalDragScroll";
import Pagination from "../components/common/Pagination";

const ExpenseManagement: React.FC = () => {
  const tableScrollRef = useHorizontalDragScroll<HTMLDivElement>();
  const { user } = useAuth();
  const [expenses, setExpenses] = useState<Expense[]>([]);
  const [branches, setBranches] = useState<Branch[]>([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [filters, setFilters] = useState<ExpenseFilters>({
    page: 1,
    limit: 10,
    sortBy: "date",
    sortOrder: "desc",
  });
  const [summary, setSummary] = useState<any>(null);
  const [pagination, setPagination] = useState<any>(null);

  // Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | null>(null);

  // Form states
  const [formData, setFormData] = useState<CreateExpenseData>({
    branchId: "",
    date: new Date().toISOString().split("T")[0],
    category: "Miscellaneous",
    amount: 0,
    description: "",
    paymentMethod: "cash",
  });

  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  // Load expenses on component mount and filter changes
  useEffect(() => {
    loadExpenses();
  }, [filters]);

  // Load branches on component mount for superadmin
  useEffect(() => {
    if (user?.roles?.includes("superadmin")) {
      loadBranches();
    }
  }, [user]);

  const loadBranches = async () => {
    try {
      const response = await getBranches();
      setBranches(response.data);
    } catch (error: any) {
      toast.error(error.message || "Failed to load branches");
    }
  };

  const loadExpenses = async () => {
    try {
      setLoading(true);
      const response = await expenseService.getExpenses(filters);
      setExpenses(response.data);
      setSummary(response.summary);
      setPagination(response.pagination);
    } catch (error: any) {
      toast.error(error.message || "Failed to load expenses");
    } finally {
      setLoading(false);
    }
  };

  const handleCreateExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      // For superadmin, validate branch selection
      if (user?.roles?.includes("superadmin") && !formData.branchId) {
        toast.error("Please select a branch or choose School-wide");
        return;
      }

      // Prepare submit data without branchId for school-wide expenses
      const submitData = { ...formData };

      if (formData.branchId === "school-wide" || !formData.branchId) {
        // For school-wide expenses, don't include branchId in the payload
        delete submitData.branchId;
      }

      await expenseService.createExpense(submitData);
      toast.success("Expense created successfully");
      setShowCreateModal(false);
      resetForm();
      loadExpenses();
    } catch (error: any) {
      toast.error(error.message || "Failed to create expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateExpense = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedExpense) return;

    setIsSubmitting(true);
    try {
      // For superadmin, validate branch selection
      if (user?.roles?.includes("superadmin") && !formData.branchId) {
        toast.error("Please select a branch or choose School-wide");
        return;
      }

      // Prepare submit data without branchId for school-wide expenses
      const submitData = { ...formData };

      if (formData.branchId === "school-wide" || !formData.branchId) {
        // For school-wide expenses, don't include branchId in the payload
        delete submitData.branchId;
      }

      await expenseService.updateExpense(selectedExpense._id, submitData);
      toast.success("Expense updated successfully");
      setShowEditModal(false);
      resetForm();
      loadExpenses();
    } catch (error: any) {
      toast.error(error.message || "Failed to update expense");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteExpense = async () => {
    if (!selectedExpense) return;

    try {
      await expenseService.deleteExpense(selectedExpense._id);
      toast.success("Expense deleted successfully");
      setShowDeleteModal(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch (error: any) {
      toast.error(error.message || "Failed to delete expense");
    }
  };

  const handleApprovalUpdate = async (
    status: ApprovalStatus,
    notes?: string
  ) => {
    if (!selectedExpense) return;

    try {
      await expenseService.updateApprovalStatus(selectedExpense._id, {
        approvalStatus: status,
        approvalNotes: notes,
      });
      toast.success(`Expense ${status} successfully`);
      setShowApprovalModal(false);
      setSelectedExpense(null);
      loadExpenses();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${status} expense`);
    }
  };

  const handleFilterChange = (key: keyof ExpenseFilters, value: any) => {
    setFilters((prev) => ({ ...prev, [key]: value, page: 1 }));
  };

  const handlePageChange = (page: number) => {
    setFilters((prev) => ({ ...prev, page }));
  };

  const handlePageSizeChange = (size: number) => {
    setFilters((prev) => ({ ...prev, limit: size, page: 1 }));
  };

  const handleSearch = () => {
    handleFilterChange("search", searchTerm);
  };

  const resetForm = () => {
    setFormData({
      branchId: "",
      date: new Date().toISOString().split("T")[0],
      category: "Miscellaneous",
      amount: 0,
      description: "",
      paymentMethod: "cash",
    });
    setSelectedExpense(null);
  };

  const openEditModal = (expense: Expense) => {
    // Check if user can edit this expense
    if (
      user?.roles?.includes("branchadmin") &&
      expense.recordedBy?._id !== user.id
    ) {
      toast.error("You can only edit expenses you created");
      return;
    }

    setSelectedExpense(expense);
    setFormData({
      branchId: expense.branchId || "school-wide", // Convert null/undefined to "school-wide"
      date: expense.date.split("T")[0],
      category: expense.category,
      subcategory: expense.subcategory,
      amount: expense.amount,
      description: expense.description,
      vendor: expense.vendor,
      paymentMethod: expense.paymentMethod,
      paymentReference: expense.paymentReference,
      receiptNumber: expense.receiptNumber,
      budgetCategory: expense.budgetCategory,
      isRecurring: expense.isRecurring,
      recurringDetails: expense.recurringDetails,
      tags: expense.tags,
      notes: expense.notes,
    });
    setShowEditModal(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-KE", {
      style: "currency",
      currency: "KES",
      currencyDisplay: "code",
    })
      .format(amount)
      .replace("KES", "KSh");
  };

  const getStatusColor = (status: ApprovalStatus) => {
    switch (status) {
      case "approved":
        return "bg-gradient-to-r from-emerald-300 to-emerald-400";
      case "rejected":
        return "bg-gradient-to-r from-red-300 to-red-400";
      case "on_hold":
        return "bg-gradient-to-r from-amber-300 to-amber-400";
      default:
        return "bg-gradient-to-r from-slate-300 to-slate-400";
    }
  };

  const categories: ExpenseCategory[] = [
    "Salaries",
    "Utilities",
    "Maintenance",
    "Supplies",
    "Transport",
    "Marketing",
    "Insurance",
    "Equipment",
    "Food & Catering",
    "Professional Services",
    "Rent",
    "Communication",
    "Training",
    "Technology",
    "Security",
    "Cleaning",
    "Medical",
    "Miscellaneous",
  ];

  const paymentMethods: PaymentMethod[] = [
    "cash",
    "bank_transfer",
    "cheque",
    "mpesa",
    "card",
    "other",
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="flex items-center gap-4">
          <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-4 shadow-lg">
            <DollarSign className="h-8 w-8 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-slate-900">
              Expense Management
            </h1>
            <p className="text-slate-600 font-semibold">
              Manage and track all expenses
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-3 shadow-md"
        >
          <Plus className="w-5 h-5 text-emerald-500" />
          <span>Add Expense</span>
        </button>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">
                  Total Expenses
                </p>
                <p className="text-xl font-bold text-slate-900 mt-2">
                  {formatCurrency(summary.totalAmount)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-2 shadow-lg">
                <DollarSign className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">
                  Pending Approval
                </p>
                <p className="text-3xl font-bold text-amber-600 mt-2">
                  {summary.pendingCount}
                </p>
              </div>
              <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-2 shadow-lg">
                <Clock className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">Approved</p>
                <p className="text-3xl font-bold text-emerald-600 mt-2">
                  {summary.approvedCount}
                </p>
              </div>
              <div className="bg-gradient-to-br from-emerald-300 to-green-400 rounded-2xl p-2 shadow-lg">
                <Check className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6 hover:shadow-2xl hover:scale-105 transition-all duration-300">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-900">
                  Average Amount
                </p>
                <p className="text-xl font-bold text-slate-900 mt-2">
                  {formatCurrency(summary.avgAmount)}
                </p>
              </div>
              <div className="bg-gradient-to-br from-blue-300 to-indigo-400 rounded-2xl p-2 shadow-lg">
                <FileText className="h-4 w-4 text-white" />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 flex gap-3">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search expenses..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && handleSearch()}
                className="px-4 py-3 w-full bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
              />
            </div>
            <button
              onClick={handleSearch}
              className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3 rounded-xl font-bold hover:shadow-lg hover:scale-105 transition-all duration-300"
            >
              Search
            </button>
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="bg-white border-2 border-slate-200 rounded-xl px-4 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-3"
          >
            <Filter className="w-5 h-5 text-purple-500" />
            <span>Filters</span>
          </button>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mt-6 pt-6 border-t border-slate-200">
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              {user?.roles?.includes("superadmin") && (
                <div>
                  <label className="block text-sm font-bold text-slate-900 mb-2">
                    Branch
                  </label>
                  <select
                    value={filters.branchId || ""}
                    onChange={(e) =>
                      handleFilterChange(
                        "branchId",
                        e.target.value || undefined
                      )
                    }
                    className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                  >
                    <option value="">All Expenses</option>
                    <option value="school-wide">🏫 School-wide Only</option>
                    {branches.map((branch) => (
                      <option key={branch._id} value={branch._id}>
                        {branch.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Category
                </label>
                <select
                  value={filters.category || ""}
                  onChange={(e) =>
                    handleFilterChange("category", e.target.value || undefined)
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                >
                  <option value="">All Categories</option>
                  {categories.map((category) => (
                    <option key={category} value={category}>
                      {category}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Status
                </label>
                <select
                  value={filters.approvalStatus || ""}
                  onChange={(e) =>
                    handleFilterChange(
                      "approvalStatus",
                      e.target.value || undefined
                    )
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                >
                  <option value="">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                  <option value="on_hold">On Hold</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  Start Date
                </label>
                <input
                  type="date"
                  value={filters.startDate || ""}
                  onChange={(e) =>
                    handleFilterChange("startDate", e.target.value || undefined)
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-slate-900 mb-2">
                  End Date
                </label>
                <input
                  type="date"
                  value={filters.endDate || ""}
                  onChange={(e) =>
                    handleFilterChange("endDate", e.target.value || undefined)
                  }
                  className="w-full px-4 py-3 bg-gradient-to-r from-slate-50 to-blue-50 border-2 border-slate-200/60 rounded-xl font-semibold text-slate-900 focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent transition-all"
                />
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <button
                onClick={() =>
                  setFilters({
                    page: 1,
                    limit: 10,
                    sortBy: "date",
                    sortOrder: "desc",
                  })
                }
                className="bg-white border-2 border-slate-200 rounded-xl px-6 py-3 font-bold text-slate-700 hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center gap-2"
              >
                <X className="w-5 h-5 text-red-500" />
                Clear Filters
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Expenses Table */}
      <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-slate-200/60 shadow-xl overflow-hidden">
        <div ref={tableScrollRef} className="overflow-x-auto">
          <table className="min-w-full divide-y divide-slate-200">
            <thead className="bg-gradient-to-r from-slate-50 to-blue-50">
              <tr>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-blue-500" />
                    Date
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-purple-500" />
                    Branch
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FileText className="w-4 h-4 text-indigo-500" />
                    Description
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <Tag className="w-4 h-4 text-cyan-500" />
                    Category
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-emerald-500" />
                    Amount
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <CreditCard className="w-4 h-4 text-pink-500" />
                    Payment
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-amber-500" />
                    Status
                  </div>
                </th>
                <th className="px-6 py-4 text-left text-xs font-bold text-slate-900 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {expenses.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-6 py-16 text-center">
                    <div className="flex flex-col items-center gap-4">
                      <div className="bg-gradient-to-br from-amber-300 to-orange-400 rounded-2xl p-6 shadow-lg">
                        <FileText className="w-12 h-12 text-white" />
                      </div>
                      <p className="text-slate-500 font-semibold text-lg">
                        {loading ? "Loading expenses..." : "No expenses found"}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                expenses
                  .filter((expense) => {
                    // Filter out expenses that secretaries and branch admins didn't create
                    if (
                      user?.roles?.includes("branchadmin") ||
                      user?.roles?.includes("secretary")
                    ) {
                      return expense.recordedBy?._id === user.id;
                    }
                    // Other roles can see all expenses
                    return true;
                  })
                  .map((expense) => (
                    <tr
                      key={expense._id}
                      className="hover:bg-gradient-to-r hover:from-slate-50 hover:to-blue-50/50 transition-all duration-300"
                    >
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-semibold text-slate-900">
                          {new Date(expense.date).toLocaleDateString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {expense.branchId ? (
                          <span className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-slate-300 to-slate-400 text-white rounded-xl text-xs font-bold shadow-md">
                            {branches.find((b) => b._id === expense.branchId)
                              ?.name || "Unknown Branch"}
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-purple-300 to-purple-400 text-white rounded-xl text-xs font-bold shadow-md">
                            🏫 School-wide
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div
                          className="max-w-xs truncate text-sm font-semibold text-slate-900"
                          title={expense.description}
                        >
                          {expense.description}
                        </div>
                        {expense.vendor?.name && (
                          <div className="text-xs text-slate-500 mt-1">
                            Vendor: {expense.vendor.name}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-3 py-1.5 bg-gradient-to-r from-cyan-300 to-cyan-400 text-white rounded-xl text-xs font-bold shadow-md">
                          {expense.category}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-emerald-600">
                        {formatCurrency(expense.amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-slate-700">
                        {expense.paymentMethod.replace("_", " ").toUpperCase()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-3 py-1.5 rounded-xl text-xs font-bold shadow-md text-white ${getStatusColor(
                            expense.approvalStatus
                          )}`}
                        >
                          {expense.approvalStatus
                            .replace("_", " ")
                            .toUpperCase()}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              // Check if user can view this expense
                              if (
                                (user?.roles?.includes("branchadmin") ||
                                  user?.roles?.includes("secretary")) &&
                                expense.recordedBy?._id !== user?.id
                              ) {
                                toast.error(
                                  "You can only view expenses you created"
                                );
                                return;
                              }
                              setSelectedExpense(expense);
                              setShowViewModal(true);
                            }}
                            className={`bg-white border-2 ${
                              (user?.roles?.includes("branchadmin") ||
                                user?.roles?.includes("secretary")) &&
                              expense.recordedBy?._id !== user?.id
                                ? "border-slate-100 cursor-not-allowed opacity-50"
                                : "border-slate-200 hover:shadow-lg hover:scale-110"
                            } rounded-xl p-2 transition-all duration-300`}
                            title={
                              (user?.roles?.includes("branchadmin") ||
                                user?.roles?.includes("secretary")) &&
                              expense.recordedBy?._id !== user?.id
                                ? "Can only view expenses you created"
                                : "View"
                            }
                          >
                            <Eye
                              className={`w-4 h-4 ${
                                (user?.roles?.includes("branchadmin") ||
                                  user?.roles?.includes("secretary")) &&
                                expense.recordedBy?._id !== user?.id
                                  ? "text-slate-400"
                                  : "text-blue-500"
                              }`}
                            />
                          </button>
                          <button
                            onClick={() => openEditModal(expense)}
                            className={`bg-white border-2 ${
                              user?.roles?.includes("branchadmin") &&
                              expense.recordedBy?._id !== user?.id
                                ? "border-slate-100 cursor-not-allowed opacity-50"
                                : "border-slate-200 hover:shadow-lg hover:scale-110"
                            } rounded-xl p-2 transition-all duration-300`}
                            title={
                              user?.roles?.includes("branchadmin") &&
                              expense.recordedBy?._id !== user?.id
                                ? "You can only edit expenses you created"
                                : "Edit"
                            }
                            disabled={
                              user?.roles?.includes("branchadmin") &&
                              expense.recordedBy?._id !== user?.id
                            }
                          >
                            <Edit
                              className={`w-4 h-4 ${
                                user?.roles?.includes("branchadmin") &&
                                expense.recordedBy?._id !== user?.id
                                  ? "text-slate-400"
                                  : "text-emerald-500"
                              }`}
                            />
                          </button>
                          {expense.approvalStatus === "pending" &&
                            (user?.roles?.includes("superadmin") ||
                              user?.roles?.includes("admin") ||
                              user?.roles?.includes("secretary") ||
                              user?.roles?.includes("branchadmin")) && (
                              <button
                                onClick={() => {
                                  setSelectedExpense(expense);
                                  setShowApprovalModal(true);
                                }}
                                className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-110 transition-all duration-300"
                                title="Approve/Reject"
                              >
                                <Clock className="w-4 h-4 text-amber-500" />
                              </button>
                            )}
                          <button
                            onClick={() => {
                              setSelectedExpense(expense);
                              setShowDeleteModal(true);
                            }}
                            className="bg-white border-2 border-slate-200 rounded-xl p-2 hover:shadow-lg hover:scale-110 transition-all duration-300"
                            title="Delete"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pagination && (
          <Pagination
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            onPageChange={handlePageChange}
            pageSize={filters.limit}
            onPageSizeChange={handlePageSizeChange}
            totalItems={pagination.totalItems}
            showPageSizeSelector={true}
          />
        )}
      </div>

      {/* Create Expense Modal */}
      {showCreateModal && (
        <ExpenseModal
          title="Create New Expense"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleCreateExpense}
          onClose={() => {
            setShowCreateModal(false);
            resetForm();
          }}
          categories={categories}
          paymentMethods={paymentMethods}
          branches={branches}
          isSuperAdmin={user?.roles?.includes("superadmin")}
          isSubmitting={isSubmitting}
        />
      )}

      {/* Edit Expense Modal */}
      {showEditModal && selectedExpense && (
        <ExpenseModal
          title="Edit Expense"
          formData={formData}
          setFormData={setFormData}
          onSubmit={handleUpdateExpense}
          onClose={() => {
            setShowEditModal(false);
            resetForm();
          }}
          categories={categories}
          paymentMethods={paymentMethods}
          branches={branches}
          isSuperAdmin={user?.roles?.includes("superadmin")}
          isSubmitting={isSubmitting}
        />
      )}

      {/* View Expense Modal */}
      {showViewModal && selectedExpense && (
        <ViewExpenseModal
          expense={selectedExpense}
          onClose={() => {
            setShowViewModal(false);
            setSelectedExpense(null);
          }}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Approval Modal */}
      {showApprovalModal && selectedExpense && (
        <ApprovalModal
          expense={selectedExpense}
          onApprove={(notes: string) => handleApprovalUpdate("approved", notes)}
          onReject={(notes: string) => handleApprovalUpdate("rejected", notes)}
          onHold={(notes: string) => handleApprovalUpdate("on_hold", notes)}
          onClose={() => {
            setShowApprovalModal(false);
            setSelectedExpense(null);
          }}
          formatCurrency={formatCurrency}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && selectedExpense && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Delete Expense
            </h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this expense? This action cannot
              be undone.
            </p>
            <div className="flex justify-end space-x-4">
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setSelectedExpense(null);
                }}
                className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteExpense}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Expense Modal Component
interface ExpenseModalProps {
  title: string;
  formData: CreateExpenseData;
  setFormData: React.Dispatch<React.SetStateAction<CreateExpenseData>>;
  onSubmit: (e: React.FormEvent) => void;
  onClose: () => void;
  categories: ExpenseCategory[];
  paymentMethods: PaymentMethod[];
  branches?: Branch[];
  isSuperAdmin?: boolean;
  isSubmitting?: boolean;
}

const ExpenseModal: React.FC<ExpenseModalProps> = ({
  title,
  formData,
  setFormData,
  onSubmit,
  onClose,
  categories,
  paymentMethods,
  branches = [],
  isSuperAdmin = false,
  isSubmitting = false,
}) => {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="space-y-4">
          {/* Branch Selection for SuperAdmin */}
          {isSuperAdmin && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Branch *
              </label>
              <select
                value={formData.branchId || ""}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    branchId: e.target.value || undefined,
                  })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                <option value="">Select a branch</option>
                <option value="school-wide">
                  🏫 School-wide (All Branches)
                </option>
                {branches.map((branch) => (
                  <option key={branch._id} value={branch._id}>
                    {branch.name}
                  </option>
                ))}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                Choose "School-wide" for expenses that affect the entire school
                rather than a specific branch
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Date *
              </label>
              <input
                type="date"
                value={formData.date}
                onChange={(e) =>
                  setFormData({ ...formData, date: e.target.value })
                }
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Category *
              </label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    category: e.target.value as ExpenseCategory,
                  })
                }
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                {categories.map((category) => (
                  <option key={category} value={category}>
                    {category}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount *
              </label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={formData.amount}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    amount: parseFloat(e.target.value) || 0,
                  })
                }
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Method *
              </label>
              <select
                value={formData.paymentMethod}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    paymentMethod: e.target.value as PaymentMethod,
                  })
                }
                required
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              >
                {paymentMethods.map((method) => (
                  <option key={method} value={method}>
                    {method.replace("_", " ").toUpperCase()}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description *
            </label>
            <textarea
              value={formData.description}
              onChange={(e) =>
                setFormData({ ...formData, description: e.target.value })
              }
              required
              rows={3}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Enter expense description"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Subcategory
            </label>
            <input
              type="text"
              value={formData.subcategory || ""}
              onChange={(e) =>
                setFormData({ ...formData, subcategory: e.target.value })
              }
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Enter subcategory (optional)"
            />
          </div>

          {/* Vendor Information */}
          <div className="border-t pt-4">
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Vendor Information (Optional)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Vendor Name
                </label>
                <input
                  type="text"
                  value={formData.vendor?.name || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vendor: { ...formData.vendor, name: e.target.value },
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Phone
                </label>
                <input
                  type="tel"
                  value={formData.vendor?.contact?.phone || ""}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      vendor: {
                        ...formData.vendor,
                        contact: {
                          ...formData.vendor?.contact,
                          phone: e.target.value,
                        },
                      },
                    })
                  }
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Payment Reference
              </label>
              <input
                type="text"
                value={formData.paymentReference || ""}
                onChange={(e) =>
                  setFormData({ ...formData, paymentReference: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
                placeholder="Transaction ID, Check Number, etc."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Receipt Number
              </label>
              <input
                type="text"
                value={formData.receiptNumber || ""}
                onChange={(e) =>
                  setFormData({ ...formData, receiptNumber: e.target.value })
                }
                className="w-full border border-gray-300 rounded-lg px-3 py-2"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              value={formData.notes || ""}
              onChange={(e) =>
                setFormData({ ...formData, notes: e.target.value })
              }
              rows={2}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Additional notes (optional)"
            />
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting
                ? "Saving..."
                : title.includes("Create")
                ? "Create Expense"
                : "Update Expense"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// View Expense Modal Component
interface ViewExpenseModalProps {
  expense: Expense;
  onClose: () => void;
  formatCurrency: (amount: number) => string;
}

const ViewExpenseModal: React.FC<ViewExpenseModalProps> = ({
  expense,
  onClose,
  formatCurrency,
}) => {
  const getStatusColor = (status: ApprovalStatus) => {
    switch (status) {
      case "approved":
        return "bg-green-100 text-green-800";
      case "rejected":
        return "bg-red-100 text-red-800";
      case "on_hold":
        return "bg-yellow-100 text-yellow-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Expense Details
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Basic Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Date
                </label>
                <p className="text-sm text-gray-900">
                  {new Date(expense.date).toLocaleDateString()}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Category
                </label>
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {expense.category}
                </span>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Amount
                </label>
                <p className="text-lg font-semibold text-gray-900">
                  {formatCurrency(expense.amount)}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Status
                </label>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                    expense.approvalStatus
                  )}`}
                >
                  {expense.approvalStatus.replace("_", " ").toUpperCase()}
                </span>
              </div>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-500">
              Description
            </label>
            <p className="text-sm text-gray-900 mt-1">{expense.description}</p>
          </div>

          {/* Payment Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Payment Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Payment Method
                </label>
                <p className="text-sm text-gray-900">
                  {expense.paymentMethod.replace("_", " ").toUpperCase()}
                </p>
              </div>
              {expense.paymentReference && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Payment Reference
                  </label>
                  <p className="text-sm text-gray-900">
                    {expense.paymentReference}
                  </p>
                </div>
              )}
              {expense.receiptNumber && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Receipt Number
                  </label>
                  <p className="text-sm text-gray-900">
                    {expense.receiptNumber}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Vendor Information */}
          {expense.vendor?.name && (
            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-3">
                Vendor Information
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Vendor Name
                  </label>
                  <p className="text-sm text-gray-900">{expense.vendor.name}</p>
                </div>
                {expense.vendor.contact?.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-500">
                      Contact Phone
                    </label>
                    <p className="text-sm text-gray-900">
                      {expense.vendor.contact.phone}
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Approval Information */}
          <div>
            <h4 className="text-sm font-medium text-gray-900 mb-3">
              Approval Information
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-500">
                  Recorded By
                </label>
                <p className="text-sm text-gray-900">
                  {expense.recordedBy.firstName} {expense.recordedBy.lastName}
                </p>
              </div>
              {expense.approvedBy && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Approved By
                  </label>
                  <p className="text-sm text-gray-900">
                    {expense.approvedBy.firstName} {expense.approvedBy.lastName}
                  </p>
                </div>
              )}
              {expense.approvalDate && (
                <div>
                  <label className="block text-sm font-medium text-gray-500">
                    Approval Date
                  </label>
                  <p className="text-sm text-gray-900">
                    {new Date(expense.approvalDate).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
            {expense.approvalNotes && (
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-500">
                  Approval Notes
                </label>
                <p className="text-sm text-gray-900 mt-1">
                  {expense.approvalNotes}
                </p>
              </div>
            )}
          </div>

          {expense.notes && (
            <div>
              <label className="block text-sm font-medium text-gray-500">
                Notes
              </label>
              <p className="text-sm text-gray-900 mt-1">{expense.notes}</p>
            </div>
          )}

          {/* Tags */}
          {expense.tags.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-2">
                Tags
              </label>
              <div className="flex flex-wrap gap-2">
                {expense.tags.map((tag, index) => (
                  <span
                    key={index}
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end pt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

// Approval Modal Component
interface ApprovalModalProps {
  expense: Expense;
  onApprove: (notes: string) => void;
  onReject: (notes: string) => void;
  onHold: (notes: string) => void;
  onClose: () => void;
  formatCurrency: (amount: number) => string;
}

const ApprovalModal: React.FC<ApprovalModalProps> = ({
  expense,
  onApprove,
  onReject,
  onHold,
  onClose,
  formatCurrency,
}) => {
  const [notes, setNotes] = useState("");
  const [action, setAction] = useState<"approve" | "reject" | "hold">(
    "approve"
  );

  const handleSubmit = () => {
    switch (action) {
      case "approve":
        onApprove(notes);
        break;
      case "reject":
        onReject(notes);
        break;
      case "hold":
        onHold(notes);
        break;
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-lg">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Expense Approval
          </h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="space-y-4">
          {/* Expense Summary */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="font-medium text-gray-900 mb-2">Expense Summary</h4>
            <div className="text-sm text-gray-600 space-y-1">
              <p>
                <strong>Amount:</strong> {formatCurrency(expense.amount)}
              </p>
              <p>
                <strong>Category:</strong> {expense.category}
              </p>
              <p>
                <strong>Description:</strong> {expense.description}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(expense.date).toLocaleDateString()}
              </p>
            </div>
          </div>

          {/* Action Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Action
            </label>
            <div className="space-y-2">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="action"
                  value="approve"
                  checked={action === "approve"}
                  onChange={() => setAction("approve")}
                  className="mr-2"
                />
                <Check className="w-4 h-4 text-green-600 mr-1" />
                <span>Approve</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="action"
                  value="reject"
                  checked={action === "reject"}
                  onChange={() => setAction("reject")}
                  className="mr-2"
                />
                <X className="w-4 h-4 text-red-600 mr-1" />
                <span>Reject</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="action"
                  value="hold"
                  checked={action === "hold"}
                  onChange={() => setAction("hold")}
                  className="mr-2"
                />
                <Clock className="w-4 h-4 text-yellow-600 mr-1" />
                <span>Put on Hold</span>
              </label>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes {action === "reject" ? "(Required)" : "(Optional)"}
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              required={action === "reject"}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              placeholder="Add notes about your decision..."
            />
          </div>
        </div>

        <div className="flex justify-end space-x-4 pt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={action === "reject" && !notes.trim()}
            className={`px-4 py-2 text-white rounded-lg ${
              action === "approve"
                ? "bg-green-600 hover:bg-green-700"
                : action === "reject"
                ? "bg-red-600 hover:bg-red-700"
                : "bg-yellow-600 hover:bg-yellow-700"
            } disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {action === "approve"
              ? "Approve"
              : action === "reject"
              ? "Reject"
              : "Put on Hold"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseManagement;
